import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import {
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
  useState,
  ReactNode,
} from 'react';

export interface TipTapEditorHandle {
  insertContent: (content: string) => void;
  getEditor: () => Editor | null;
}

/** Images an email client will reliably render. SVG is excluded deliberately. */
export const DEFAULT_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
];

export const DEFAULT_MAX_IMAGE_BYTES = 5 * 1024 * 1024;

interface TipTapEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
  /** Slot rendered on the right of the toolbar (e.g. "Insert token" button). */
  extraToolbarSlot?: ReactNode;
  /**
   * Upload handler for inline images. Returns the hosted URL to embed.
   *
   * When omitted the image button is hidden and paste/drop of image files is
   * left to the default handling — existing call sites (Terms, Privacy,
   * message templates) are unaffected.
   *
   * Images are NEVER embedded as base64 data URLs: Gmail and Outlook block or
   * strip them, and on a BCC broadcast the bytes are re-sent per batch. The
   * backend rejects a body containing `src="data:"` outright.
   */
  onImageUpload?: (file: File) => Promise<string>;
  /** Surface upload/validation failures to the caller (toast, inline error…). */
  onImageUploadError?: (error: Error, file: File) => void;
  maxImageBytes?: number;
  acceptedImageTypes?: string[];
}

const TipTapEditor = forwardRef<TipTapEditorHandle, TipTapEditorProps>(
  (
    {
      value = '',
      onChange,
      placeholder = 'Start typing...',
      minHeight = '300px',
      extraToolbarSlot,
      onImageUpload,
      onImageUploadError,
      maxImageBytes = DEFAULT_MAX_IMAGE_BYTES,
      acceptedImageTypes = DEFAULT_IMAGE_TYPES,
    },
    ref
  ) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    // Read inside editor callbacks, which capture their first closure.
    const uploadRef = useRef(onImageUpload);
    uploadRef.current = onImageUpload;

    const editor = useEditor({
      extensions: [
        // StarterKit v3 ALREADY bundles and registers Link — importing
        // @tiptap/extension-link separately registered it twice, which logs a
        // "Duplicate extension names found" warning and lets the two configs
        // race. Configure it through StarterKit instead; this also drops a
        // phantom dependency that was only resolving transitively.
        StarterKit.configure({
          link: {
            openOnClick: false,
            autolink: true,
            HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
          },
        }),
        // Registered unconditionally, not just when onImageUpload is supplied:
        // without it the ProseMirror schema silently STRIPS any <img> already
        // present in a loaded document, which quietly ate images in the
        // Terms/Privacy and message-template editors.
        Image.configure({
          inline: false,
          // Hard stop on base64 at the editor boundary, so a Word/Outlook paste
          // cannot smuggle a data: URI into an email body.
          allowBase64: false,
          HTMLAttributes: { style: 'max-width:100%;height:auto;' },
        }),
        // Inline style="text-align: …" on heading/paragraph — universally
        // honoured by email clients (Gmail, Apple Mail, Outlook).
        TextAlign.configure({
          types: ['heading', 'paragraph'],
          alignments: ['left', 'center', 'right'],
          defaultAlignment: 'left',
        }),
      ],
      content: value,
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        onChange?.(html);
      },
      editorProps: {
        attributes: {
          class:
            'tiptap-editor focus:outline-none p-4 min-h-full text-gray-900 dark:text-gray-100',
        },
        handlePaste: (_view, event) => handleImageEvent(event.clipboardData),
        handleDrop: (_view, event) => handleImageEvent(
          (event as DragEvent).dataTransfer
        ),
      },
    });

    /**
     * Validate then upload image files, inserting each as a hosted URL.
     * Returns false when there is nothing to handle so ProseMirror falls
     * through to its default paste/drop behaviour (HTML paste, autolink, …).
     */
    const handleImageFiles = async (files: File[]) => {
      const upload = uploadRef.current;
      if (!upload || files.length === 0) return;

      setIsUploadingImage(true);
      try {
        for (const file of files) {
          if (acceptedImageTypes.indexOf(file.type) === -1) {
            onImageUploadError?.(
              new Error(`${file.name}: unsupported image type (${file.type || 'unknown'})`),
              file
            );
            continue;
          }
          if (file.size > maxImageBytes) {
            const mb = Math.round(maxImageBytes / (1024 * 1024));
            onImageUploadError?.(
              new Error(`${file.name} is too large (max ${mb}MB)`),
              file
            );
            continue;
          }
          try {
            const url = await upload(file);
            editor
              ?.chain()
              .focus()
              .setImage({ src: url, alt: file.name })
              .run();
          } catch (err) {
            // Surfaced, not swallowed into console.error — the admin needs to
            // know the image is not in their email.
            onImageUploadError?.(
              err instanceof Error ? err : new Error('Image upload failed'),
              file
            );
          }
        }
      } finally {
        setIsUploadingImage(false);
      }
    };

    /** Shared paste/drop entry point. */
    function handleImageEvent(data: DataTransfer | null): boolean {
      if (!uploadRef.current || !data || !data.files || data.files.length === 0) {
        return false;
      }
      const images = Array.from(data.files).filter((f) =>
        f.type.startsWith('image/')
      );
      if (images.length === 0) return false;
      handleImageFiles(images);
      return true; // swallow the event; we insert the hosted URL ourselves
    }

    useImperativeHandle(
      ref,
      () => ({
        insertContent: (content: string) => {
          editor?.chain().focus().insertContent(content).run();
        },
        getEditor: () => editor,
      }),
      [editor]
    );

    useEffect(() => {
      if (editor && value !== editor.getHTML()) {
        // emitUpdate:false — otherwise this write re-fires onUpdate, which
        // calls the parent's setState, which feeds a new `value` back in. That
        // round-trip resets the cursor and can land mid-image-insert.
        editor.commands.setContent(value, { emitUpdate: false });
      }
    }, [value, editor]);

    if (!editor) {
      return null;
    }

    const promptForLink = () => {
      const prev = editor.getAttributes('link').href as string | undefined;
      const url = window.prompt('Link URL', prev || 'https://');
      if (url === null) return;
      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    const btnBase =
      'shrink-0 px-2.5 py-1.5 rounded text-sm font-medium transition-colors border whitespace-nowrap';
    const btnActive =
      'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700';
    const btnIdle =
      'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700';

    return (
      <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
        <style>
          {`
            .tiptap-editor h1 { font-size: 1.75rem; font-weight: 700; margin: 0.5rem 0; line-height: 1.25; }
            .tiptap-editor h2 { font-size: 1.375rem; font-weight: 700; margin: 0.5rem 0; line-height: 1.3; }
            .tiptap-editor p { margin: 0.75rem 0; }
            .tiptap-editor p:first-child { margin-top: 0; }
            .tiptap-editor p:last-child { margin-bottom: 0; }
            .tiptap-editor strong { font-weight: 700; }
            .tiptap-editor em { font-style: italic; }
            .tiptap-editor a { color: #5C68E2; text-decoration: underline; cursor: pointer; }
            .dark .tiptap-editor a { color: #818cf8; }
            .tiptap-editor ul { list-style-type: disc; margin-left: 1.5rem; margin: 0.75rem 0 0.75rem 1.5rem; }
            .tiptap-editor ol { list-style-type: decimal; margin: 0.75rem 0 0.75rem 1.5rem; }
            .tiptap-editor li { margin: 0.25rem 0; }
          `}
        </style>

        {/* Toolbar — wraps on desktop, horizontally scrolls on narrow screens */}
        <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2 flex items-center gap-2">
          <div className="flex flex-wrap md:flex-nowrap md:overflow-x-auto gap-1 flex-1 min-w-0">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`${btnBase} ${editor.isActive('bold') ? btnActive : btnIdle}`}
              title="Bold"
              aria-label="Bold"
            >
              <span className="font-bold">B</span>
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`${btnBase} ${editor.isActive('italic') ? btnActive : btnIdle}`}
              title="Italic"
              aria-label="Italic"
            >
              <span className="italic">I</span>
            </button>
            <button
              type="button"
              onClick={promptForLink}
              className={`${btnBase} ${editor.isActive('link') ? btnActive : btnIdle}`}
              title="Link"
              aria-label="Link"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 015.656 5.656l-3 3a4 4 0 01-5.656-5.656m-2.828-2.828a4 4 0 00-5.656 5.656l3 3a4 4 0 005.656-5.656" />
              </svg>
            </button>

            {/* Image button — only when the caller can actually store the file.
                Without an upload target the only option would be a base64 data
                URL, which email clients block. */}
            {onImageUpload && (
              <>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingImage}
                  className={`${btnBase} ${btnIdle} disabled:opacity-50 disabled:cursor-not-allowed`}
                  title="Insert image"
                  aria-label="Insert image"
                  data-testid="tiptap-image-button"
                >
                  {isUploadingImage ? (
                    <span className="text-xs">Uploading…</span>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={acceptedImageTypes.join(',')}
                  multiple
                  className="hidden"
                  data-testid="tiptap-image-input"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    // Reset so re-picking the same file fires change again.
                    e.target.value = '';
                    handleImageFiles(files);
                  }}
                />
              </>
            )}

            <div className="shrink-0 w-px h-7 bg-gray-300 dark:bg-gray-600 mx-1 self-center" />

            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              className={`${btnBase} ${editor.isActive('heading', { level: 1 }) ? btnActive : btnIdle}`}
              title="Heading 1"
              aria-label="Heading 1"
            >
              H1
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={`${btnBase} ${editor.isActive('heading', { level: 2 }) ? btnActive : btnIdle}`}
              title="Heading 2"
              aria-label="Heading 2"
            >
              H2
            </button>

            <div className="shrink-0 w-px h-7 bg-gray-300 dark:bg-gray-600 mx-1 self-center" />

            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              className={`${btnBase} ${editor.isActive({ textAlign: 'left' }) ? btnActive : btnIdle}`}
              title="Align Left"
              aria-label="Align Left"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h14" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
              className={`${btnBase} ${editor.isActive({ textAlign: 'center' }) ? btnActive : btnIdle}`}
              title="Align Center"
              aria-label="Align Center"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M7 12h10M5 18h14" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              className={`${btnBase} ${editor.isActive({ textAlign: 'right' }) ? btnActive : btnIdle}`}
              title="Align Right"
              aria-label="Align Right"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M10 12h10M6 18h14" />
              </svg>
            </button>

            <div className="shrink-0 w-px h-7 bg-gray-300 dark:bg-gray-600 mx-1 self-center" />

            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`${btnBase} ${editor.isActive('bulletList') ? btnActive : btnIdle}`}
              title="Bullet List"
              aria-label="Bullet List"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={`${btnBase} ${editor.isActive('orderedList') ? btnActive : btnIdle}`}
              title="Numbered List"
              aria-label="Numbered List"
            >
              <span className="text-xs font-mono">1.</span>
            </button>

            <div className="shrink-0 w-px h-7 bg-gray-300 dark:bg-gray-600 mx-1 self-center" />

            <button
              type="button"
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
              className={`${btnBase} ${btnIdle} disabled:opacity-50 disabled:cursor-not-allowed`}
              title="Undo"
              aria-label="Undo"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
              className={`${btnBase} ${btnIdle} disabled:opacity-50 disabled:cursor-not-allowed`}
              title="Redo"
              aria-label="Redo"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
              </svg>
            </button>
          </div>

          {extraToolbarSlot && (
            <div className="shrink-0 flex items-center">{extraToolbarSlot}</div>
          )}
        </div>

        {/* Editor content */}
        <div
          className="overflow-auto relative cursor-text"
          style={{ minHeight }}
          onClick={() => editor.chain().focus().run()}
        >
          <EditorContent editor={editor} />
          {!value && (
            <div className="absolute top-4 left-6 text-gray-400 dark:text-gray-500 pointer-events-none">
              {placeholder}
            </div>
          )}
        </div>
      </div>
    );
  }
);

TipTapEditor.displayName = 'TipTapEditor';

export default TipTapEditor;
